// shared-lib/cabinet-runs/lowerCornerTreatment.js
//
// What the USER says a LOWER corner holds, and which answers the room allows.
//
// A lower corner already carries a record — `treatment`, written by the
// generator's join (`cornerTreatment.js`) and DERIVED from what the generator
// built. The fill trusts it unchecked, and must go on doing so: the generator
// measures a corner its own way (it seats a single-sided unit where this
// engine's measure of the same leg reads under 36"), so grading that record
// would change existing kitchens on their first edit. It says what IS there.
//
// The user's pick says what they WANT, so it is a different field on the same
// corner record — `userTreatment` — and it is the only one ever graded:
//
//     the user's pick, while it fits  →  the generator's record  →  the plan
//
// Clearing it ("Auto") therefore puts the corner back EXACTLY as the generator
// built it, not as the plan would now re-derive it. (The upper tier has no
// generator record, so there the user's pick IS `treatment`;
// `cornerChoice.userChoiceField` is the one place that knows the difference.)
//
// Pure. No I/O.
const { cornerOverride } = require('./bayOverrides');
const {
  CORNER_OPTIONS,
  userChoiceField,
  optionIdOf,
  shortLeg,
} = require('./cornerChoice');

/** The user's pick for lower corner `cornerIndex` of `run`, or null. */
function userLowerTreatment(run, cornerIndex) {
  const t = cornerOverride(run, cornerIndex, 'lower')?.[
    userChoiceField('lower')
  ];
  return optionIdOf(t) ? t : null;
}

/**
 * Grade every option at one lower corner, from what `openings.measureCorner`
 * measured — the SAME numbers `cornerPlan` decides on, so an option is
 * feasible exactly when the plan could have chosen it.
 *
 *  - `cabinet`: the L's doors need the 36" footprint clear of OPENINGS on both
 *    legs; a hard piece only has to leave its leg's gate (the 24" box where
 *    the packer can slide it clear, the full footprint where it cannot).
 *  - `blind`: both legs clear for the corner box, of openings and pieces
 *    alike — the extending leg's body sweeps that much of the other wall.
 *  - `empty`: always.
 *
 * @param {Object} m `openings.measureCorner`'s result
 * @returns {{id, mode, extend?, feasible: boolean, reason?: Object}[]}
 */
function gradeLowerCorner(m) {
  const openings = {
    A: { clear: m.openA, blocker: 'opening', blockerId: m.openIdA },
    B: { clear: m.openB, blocker: 'opening', blockerId: m.openIdB },
  };
  const pieces = {
    A: { clear: m.hardA, blocker: m.hardKindA },
    B: { clear: m.hardB, blocker: m.hardKindB },
  };
  const nearest = (leg) =>
    openings[leg].clear <= pieces[leg].clear ? openings[leg] : pieces[leg];
  const either = { A: nearest('A'), B: nearest('B') };
  return CORNER_OPTIONS.map((opt) => {
    let reason = null;
    if (opt.mode === 'cabinet')
      reason =
        shortLeg(openings, m.footprint) || shortLeg(pieces, m.gateA, m.gateB);
    else if (opt.mode === 'blind') reason = shortLeg(either, m.depth);
    return { ...opt, feasible: !reason, ...(reason ? { reason } : {}) };
  });
}

module.exports = { userLowerTreatment, gradeLowerCorner };
